import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  Plus, Trash2, Edit2, Save, X, Image, Upload, Calendar, MapPin,
  Loader2, AlertCircle, CheckCircle, FolderPlus, Lock, LogOut, Eye, EyeOff
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminGalleryPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddImageModal, setShowAddImageModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form states
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    date: '',
    location: '',
    images: []
  });

  const [newImage, setNewImage] = useState({
    url: '',
    alt: '',
    caption: ''
  });

  // Check if already authenticated on mount
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      verifyToken(token);
    } else {
      setAuthLoading(false);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsAuthenticated(true);
      fetchEvents();
    } catch (error) {
      localStorage.removeItem('adminToken');
      setIsAuthenticated(false);
      setLoading(false);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!credentials.username || !credentials.password) {
      toast.error('Please enter username and password');
      return;
    }

    setLoginLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      localStorage.setItem('adminToken', response.data.token);
      setIsAuthenticated(true);
      fetchEvents();
      toast.success('Login successful!');
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Invalid credentials');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      await axios.post(`${API}/admin/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Ignore logout errors
    }
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setEvents([]);
    toast.success('Logged out successfully');
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return { Authorization: `Bearer ${token}` };
  };

  // Fetch events on mount
  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/gallery/events`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load gallery events');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async () => {
    if (!newEvent.title || !newEvent.date || !newEvent.location) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/gallery/events`, newEvent, {
        headers: getAuthHeaders()
      });
      setEvents([response.data, ...events]);
      setShowCreateModal(false);
      setNewEvent({ title: '', description: '', date: '', location: '', images: [] });
      toast.success('Event created successfully!');
    } catch (error) {
      console.error('Error creating event:', error);
      if (error.response?.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to create event');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateEvent = async () => {
    if (!editingEvent) return;

    setSaving(true);
    try {
      const response = await axios.put(`${API}/gallery/events/${editingEvent.id}`, {
        title: editingEvent.title,
        description: editingEvent.description,
        date: editingEvent.date,
        location: editingEvent.location,
        images: editingEvent.images
      }, {
        headers: getAuthHeaders()
      });
      setEvents(events.map(e => e.id === editingEvent.id ? response.data : e));
      setEditingEvent(null);
      toast.success('Event updated successfully!');
    } catch (error) {
      console.error('Error updating event:', error);
      if (error.response?.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to update event');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!confirm('Are you sure you want to delete this event and all its images?')) return;

    try {
      await axios.delete(`${API}/gallery/events/${eventId}`, {
        headers: getAuthHeaders()
      });
      setEvents(events.filter(e => e.id !== eventId));
      toast.success('Event deleted successfully!');
    } catch (error) {
      console.error('Error deleting event:', error);
      if (error.response?.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to delete event');
      }
    }
  };

  const handleAddImage = async () => {
    if (!newImage.url || !newImage.alt || !newImage.caption) {
      toast.error('Please fill in all image fields');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/gallery/events/${selectedEventId}/images`, newImage, {
        headers: getAuthHeaders()
      });
      // Refresh events
      await fetchEvents();
      setShowAddImageModal(false);
      setNewImage({ url: '', alt: '', caption: '' });
      setSelectedEventId(null);
      toast.success('Image added successfully!');
    } catch (error) {
      console.error('Error adding image:', error);
      if (error.response?.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to add image');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteImage = async (eventId, imageIndex) => {
    if (!confirm('Are you sure you want to delete this image?')) return;

    try {
      await axios.delete(`${API}/gallery/events/${eventId}/images/${imageIndex}`, {
        headers: getAuthHeaders()
      });
      await fetchEvents();
      toast.success('Image deleted successfully!');
    } catch (error) {
      console.error('Error deleting image:', error);
      if (error.response?.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to delete image');
      }
    }
  };

  // Add image to new event form
  const addImageToNewEvent = () => {
    if (!newImage.url || !newImage.alt || !newImage.caption) {
      toast.error('Please fill in all image fields');
      return;
    }
    setNewEvent({
      ...newEvent,
      images: [...newEvent.images, { ...newImage }]
    });
    setNewImage({ url: '', alt: '', caption: '' });
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-card rounded-2xl border border-border p-8 shadow-xl">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Lock className="h-8 w-8 text-primary" />
              </div>
              <h1 className="text-2xl font-bold font-['Outfit'] text-foreground">Admin Login</h1>
              <p className="text-muted-foreground mt-2">Enter your credentials to access the gallery admin</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Username</label>
                <input
                  type="text"
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  className="w-full h-12 px-4 rounded-xl bg-background border border-border text-foreground focus:border-primary focus:ring-1 focus:ring-primary"
                  placeholder="Enter username"
                  autoComplete="username"
                  data-testid="admin-username"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={credentials.password}
                    onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                    className="w-full h-12 px-4 pr-12 rounded-xl bg-background border border-border text-foreground focus:border-primary focus:ring-1 focus:ring-primary"
                    placeholder="Enter password"
                    autoComplete="current-password"
                    data-testid="admin-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loginLoading}
                className="w-full h-12 rounded-xl bg-primary text-primary-foreground text-lg font-semibold"
                data-testid="admin-login-btn"
              >
                {loginLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Logging in...
                  </>
                ) : (
                  <>
                    <Lock className="mr-2 h-5 w-5" />
                    Login
                  </>
                )}
              </Button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  // Show loading while fetching events
  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Header */}
      <section className="py-8 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold font-['Outfit'] text-foreground">
                Gallery Admin
              </h1>
              <p className="text-muted-foreground mt-1">
                Manage gallery events and images
              </p>
            </div>
            <div className="flex gap-3">
              <Button 
                onClick={() => setShowCreateModal(true)}
                className="rounded-xl bg-primary text-primary-foreground"
                data-testid="create-event-btn"
              >
                <FolderPlus className="mr-2 h-5 w-5" />
                Create Event
              </Button>
              <Button 
                onClick={handleLogout}
                variant="outline"
                className="rounded-xl"
                data-testid="logout-btn"
              >
                <LogOut className="mr-2 h-5 w-5" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Events List */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          {events.length === 0 ? (
            <div className="text-center py-16 bg-card rounded-2xl border border-border">
              <Image className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No Events Yet</h3>
              <p className="text-muted-foreground mb-6">Create your first gallery event to get started</p>
              <Button onClick={() => setShowCreateModal(true)} className="rounded-xl">
                <Plus className="mr-2 h-5 w-5" />
                Create Event
              </Button>
            </div>
          ) : (
            events.map((event) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-card rounded-2xl border border-border p-6"
                data-testid={`admin-event-${event.id}`}
              >
                {/* Event Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
                  {editingEvent?.id === event.id ? (
                    <div className="flex-1 space-y-3 w-full">
                      <input
                        type="text"
                        value={editingEvent.title}
                        onChange={(e) => setEditingEvent({ ...editingEvent, title: e.target.value })}
                        className="w-full h-10 px-3 rounded-lg bg-background border border-border text-foreground"
                        placeholder="Event Title"
                      />
                      <textarea
                        value={editingEvent.description}
                        onChange={(e) => setEditingEvent({ ...editingEvent, description: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg bg-background border border-border text-foreground"
                        placeholder="Description"
                        rows={2}
                      />
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={editingEvent.date}
                          onChange={(e) => setEditingEvent({ ...editingEvent, date: e.target.value })}
                          className="flex-1 h-10 px-3 rounded-lg bg-background border border-border text-foreground"
                          placeholder="Date (e.g., January 2025)"
                        />
                        <input
                          type="text"
                          value={editingEvent.location}
                          onChange={(e) => setEditingEvent({ ...editingEvent, location: e.target.value })}
                          className="flex-1 h-10 px-3 rounded-lg bg-background border border-border text-foreground"
                          placeholder="Location"
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <h2 className="text-xl font-bold font-['Outfit'] text-foreground">{event.title}</h2>
                      <p className="text-muted-foreground text-sm mt-1">{event.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {event.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          {event.location}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    {editingEvent?.id === event.id ? (
                      <>
                        <Button
                          onClick={handleUpdateEvent}
                          disabled={saving}
                          size="sm"
                          className="rounded-lg bg-green-600 hover:bg-green-700"
                        >
                          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        </Button>
                        <Button
                          onClick={() => setEditingEvent(null)}
                          size="sm"
                          variant="outline"
                          className="rounded-lg"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          onClick={() => { setSelectedEventId(event.id); setShowAddImageModal(true); }}
                          size="sm"
                          variant="outline"
                          className="rounded-lg"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Image
                        </Button>
                        <Button
                          onClick={() => setEditingEvent({ ...event })}
                          size="sm"
                          variant="outline"
                          className="rounded-lg"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleDeleteEvent(event.id)}
                          size="sm"
                          variant="outline"
                          className="rounded-lg text-destructive hover:bg-destructive hover:text-destructive-foreground"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Images Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {event.images.map((image, index) => (
                    <div key={index} className="relative group">
                      <div className="aspect-square rounded-xl overflow-hidden bg-background">
                        <img
                          src={image.url}
                          alt={image.alt}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/300?text=Image+Error';
                          }}
                        />
                      </div>
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                        <Button
                          onClick={() => handleDeleteImage(event.id, index)}
                          size="sm"
                          variant="destructive"
                          className="rounded-lg"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 truncate">{image.caption}</p>
                    </div>
                  ))}
                  {event.images.length === 0 && (
                    <div className="col-span-full text-center py-8 text-muted-foreground">
                      <Image className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No images yet</p>
                    </div>
                  )}
                </div>
              </motion.div>
            ))
          )}
        </div>
      </section>

      {/* Create Event Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-card rounded-2xl border border-border p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold font-['Outfit'] text-foreground">Create New Event</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowCreateModal(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Event Title *</label>
                  <input
                    type="text"
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                    className="w-full h-10 px-3 rounded-lg bg-background border border-border text-foreground"
                    placeholder="e.g., Orphan Feeding Program - Africa"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Description</label>
                  <textarea
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg bg-background border border-border text-foreground"
                    placeholder="Brief description of the event..."
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Date *</label>
                    <input
                      type="text"
                      value={newEvent.date}
                      onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                      className="w-full h-10 px-3 rounded-lg bg-background border border-border text-foreground"
                      placeholder="e.g., January 2025"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Location *</label>
                    <input
                      type="text"
                      value={newEvent.location}
                      onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                      className="w-full h-10 px-3 rounded-lg bg-background border border-border text-foreground"
                      placeholder="e.g., Africa, Barrie, etc."
                    />
                  </div>
                </div>

                {/* Add Images Section */}
                <div className="border-t border-border pt-4 mt-4">
                  <h3 className="text-lg font-semibold text-foreground mb-3">Add Images</h3>
                  
                  <div className="space-y-3 p-4 bg-background rounded-xl">
                    <input
                      type="text"
                      value={newImage.url}
                      onChange={(e) => setNewImage({ ...newImage, url: e.target.value })}
                      className="w-full h-10 px-3 rounded-lg bg-card border border-border text-foreground"
                      placeholder="Image URL"
                    />
                    <input
                      type="text"
                      value={newImage.alt}
                      onChange={(e) => setNewImage({ ...newImage, alt: e.target.value })}
                      className="w-full h-10 px-3 rounded-lg bg-card border border-border text-foreground"
                      placeholder="Alt text (for accessibility)"
                    />
                    <input
                      type="text"
                      value={newImage.caption}
                      onChange={(e) => setNewImage({ ...newImage, caption: e.target.value })}
                      className="w-full h-10 px-3 rounded-lg bg-card border border-border text-foreground"
                      placeholder="Caption"
                    />
                    <Button
                      onClick={addImageToNewEvent}
                      variant="outline"
                      className="w-full rounded-lg"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add Image to Event
                    </Button>
                  </div>

                  {/* Preview added images */}
                  {newEvent.images.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm text-muted-foreground mb-2">{newEvent.images.length} image(s) added:</p>
                      <div className="flex flex-wrap gap-2">
                        {newEvent.images.map((img, idx) => (
                          <div key={idx} className="relative group">
                            <img
                              src={img.url}
                              alt={img.alt}
                              className="w-16 h-16 object-cover rounded-lg"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/64?text=Error';
                              }}
                            />
                            <button
                              onClick={() => setNewEvent({
                                ...newEvent,
                                images: newEvent.images.filter((_, i) => i !== idx)
                              })}
                              className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center text-xs"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => setShowCreateModal(false)} className="rounded-lg">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateEvent}
                    disabled={saving}
                    className="rounded-lg bg-primary"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Create Event
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Image Modal */}
      <AnimatePresence>
        {showAddImageModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            onClick={() => { setShowAddImageModal(false); setSelectedEventId(null); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-card rounded-2xl border border-border p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold font-['Outfit'] text-foreground">Add Image</h2>
                <Button variant="ghost" size="sm" onClick={() => { setShowAddImageModal(false); setSelectedEventId(null); }}>
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Image URL *</label>
                  <input
                    type="text"
                    value={newImage.url}
                    onChange={(e) => setNewImage({ ...newImage, url: e.target.value })}
                    className="w-full h-10 px-3 rounded-lg bg-background border border-border text-foreground"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Alt Text *</label>
                  <input
                    type="text"
                    value={newImage.alt}
                    onChange={(e) => setNewImage({ ...newImage, alt: e.target.value })}
                    className="w-full h-10 px-3 rounded-lg bg-background border border-border text-foreground"
                    placeholder="Description for accessibility"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Caption *</label>
                  <input
                    type="text"
                    value={newImage.caption}
                    onChange={(e) => setNewImage({ ...newImage, caption: e.target.value })}
                    className="w-full h-10 px-3 rounded-lg bg-background border border-border text-foreground"
                    placeholder="Image caption"
                  />
                </div>

                {/* Preview */}
                {newImage.url && (
                  <div className="mt-2">
                    <p className="text-sm text-muted-foreground mb-1">Preview:</p>
                    <img
                      src={newImage.url}
                      alt="Preview"
                      className="w-full h-32 object-cover rounded-lg"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/300x128?text=Invalid+URL';
                      }}
                    />
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-2">
                  <Button variant="outline" onClick={() => { setShowAddImageModal(false); setSelectedEventId(null); }} className="rounded-lg">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddImage}
                    disabled={saving}
                    className="rounded-lg bg-primary"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        Add Image
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminGalleryPage;
